'use client';

import { useState } from 'react';
import {
  HydrationBoundary,
  QueryClient,
  QueryClientProvider,
  DehydratedState,
} from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { handleApiError } from '@/lib/error-handler';
import { isReactQueryDevtoolsEnabled } from '@/lib/config';
import { BillingError, AgentRunLimitError } from '@/lib/api';

export function ReactQueryProvider({
  children,
  dehydratedState,
}: {
  children: React.ReactNode;
  dehydratedState?: DehydratedState;
}) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 20 * 1000,
            gcTime: 2 * 60 * 1000, 
            retry: (failureCount, error: any) => {
              // Don't retry on 4xx client errors (including 404)
              if (error?.status >= 400 && error?.status < 500) return false;
              // Don't retry on 500+ server errors (usually indicates a logic error, not transient)
              if (error?.status >= 500) return false;
              // Don't retry on network errors (status 0 or fetch failures)
              if (error?.status === 0 || error?.message?.includes('Failed to fetch') || error?.message?.includes('Load failed')) {
                return false; // Don't retry network errors - they're usually CORS or connectivity issues
              }
              return failureCount < 3;
            },
            refetchOnMount: true,
            refetchOnWindowFocus: false,
            refetchOnReconnect: 'always',
          },
          mutations: {
            retry: (failureCount, error: any) => {
              if (error?.status >= 400 && error?.status < 500) return false;
              return failureCount < 1;
            },
            onError: (error: any, variables: any, context: any) => {
              // Don't globally handle errors that are expected to be handled by components
              if (error instanceof BillingError || error instanceof AgentRunLimitError) {
                return; // Let components handle these specific errors
              }
              handleApiError(error, {
                operation: 'perform action',
                silent: false,
              });
            },
          },
        },
      }),
  );

  const showDevtools = isReactQueryDevtoolsEnabled();

  return (
    <QueryClientProvider client={queryClient}>
      <HydrationBoundary state={dehydratedState}>
        {children}
        {showDevtools && (
          <ReactQueryDevtools initialIsOpen={false} />
        )}
      </HydrationBoundary>
    </QueryClientProvider>
  );
}
